import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { css, keyframes } from '@emotion/react';
import {
  TextButton,
  Button,
  IconButton,
  IconSearch,
  IconChevronDown,
  IconEllipsis,
  IconPlus,
  IconArrowDown,
  IconArrowLeft,
  IconDownload,
  IconCross,
  IconCheck,
  IconToggle,
  IconToggleGroup,
  Checkbox,
  Pagination,
  Spinner,
  Toast,
  Modal,
} from '@flodesk/grain';
import Header from '@/components/app/Header';
import FilterModal from '@/components/app/FilterModal';
import SegmentFilterModal from '@/components/app/SegmentFilterModal';

function IconSquare() {
  return (
    <svg width="16" height="18" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.498 34A4.5 4.5 0 0 1 0 29.5v-23A4.5 4.5 0 0 1 4.5 2h23A4.5 4.5 0 0 1 32 6.5v23c0 2.484-2.01 4.5-4.497 4.5H4.498M3 29.5c0 .83.671 1.5 1.498 1.5h23.005C28.33 31 29 30.33 29 29.5v-23A1.5 1.5 0 0 0 27.5 5h-23A1.5 1.5 0 0 0 3 6.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconListView() {
  return (
    <svg width="17" height="18" viewBox="0 0 34 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 7.5A1.5 1.5 0 0 1 1.5 6h31a1.5 1.5 0 0 1 0 3h-31A1.5 1.5 0 0 1 0 7.5M0 18a1.5 1.5 0 0 1 1.5-1.5h31a1.5 1.5 0 0 1 0 3h-31A1.5 1.5 0 0 1 0 18m1.5 9a1.5 1.5 0 0 0 0 3h31a1.5 1.5 0 0 0 0-3z"
      />
    </svg>
  );
}

const pageStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const contentStyles = css`
  flex: 1;
  background: var(--grn-color-background2);
  padding: 72px;
`;

const contentInnerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 64px;
  max-width: 1140px;
  margin: 0 auto;
`;

const pageHeaderStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 40px;
`;

const tabsStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.6px;
  white-space: nowrap;
`;

const tabActiveStyles = css`
  color: var(--grn-color-content);
  cursor: pointer;
`;

const tabInactiveStyles = css`
  color: var(--grn-color-content2);
  cursor: pointer;

  &:hover {
    color: var(--grn-color-content);
  }
`;

const filtersStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const buttonWrapperStyles = css`
  padding-left: 8px;
`;

const tableStyles = css`
  display: flex;
  width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 100%;
    width: 48px;
    height: 100%;
  }

  &:hover .row-checkbox {
    opacity: 1;
  }
`;

const rowCheckboxStyles = css`
  opacity: 0;
  transition: opacity var(--grn-transition-fast);
  display: flex;
  align-items: center;
  position: absolute;
  right: 100%;
  padding-right: 12px;

  &[data-checked='true'] {
    opacity: 1;
  }
`;

const headerCheckboxRowStyles = css`
  display: flex;
  align-items: center;
  position: relative;
`;

const headerCheckboxMenuStyles = css`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: 220px;
  padding: 8px;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow:
    0px 1px 1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0px 5.88px 14.05px rgba(0, 0, 0, 0.09);
`;

const headerCheckboxMenuItemStyles = css`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const bulkActionsBarStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--grn-transition-fast),
    visibility var(--grn-transition-fast);

  &[data-visible='true'] {
    opacity: 1;
    visibility: visible;
  }
`;

const STATUS_ACTIONS: Record<string, string[]> = {
  Active: ['Delete', 'Unsubscribe', 'Archive', 'Add to segment', 'Remove from segment'],
  Unsubscribed: ['Delete', 'Add to segment', 'Remove from segment'],
  Unconfirmed: ['Delete', 'Resend opt-in'],
  'Marked spam': ['Delete', 'Add to segment', 'Remove from segment'],
  Cleaned: ['Delete', 'Add to segment', 'Remove from segment'],
  Archived: ['Delete', 'Unarchive', 'Add to segment', 'Remove from segment'],
  Bounced: ['Delete', 'Add to segment', 'Remove from segment'],
};

const BUTTON_ORDER = [
  'Delete',
  'Unsubscribe',
  'Resend opt-in',
  'Archive',
  'Unarchive',
  'Add to segment',
  'Remove from segment',
];

const highlightButtonStyles = css`
  color: var(--grn-color-content);
`;

const bulkActionsLabelStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  padding-right: 16px;
`;

const columnStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`;

const subscriberColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const statusColumnStyles = css`
  ${columnStyles};
  width: 144px;
`;

const activityColumnStyles = css`
  ${columnStyles};
  width: 136px;
  min-width: 160px;
`;

const salesColumnStyles = css`
  ${columnStyles};
  width: 104px;
`;

const segmentsColumnStyles = css`
  ${columnStyles};
  width: 320px;
  align-items: flex-end;
`;

const reasonColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const reasonRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const rowsContainerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const rowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 8px 0 0;
  border-bottom: 1px solid var(--grn-color-shade3);
  gap: 16px;
  position: relative;

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const statusRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 20px 32px 20px 0;
  border-bottom: 1px solid var(--grn-color-shade3);

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const activityRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const salesRowStyles = css`
  display: flex;
  align-items: center;
  height: 56px;
  padding-right: 32px;
  border-bottom: 1px solid var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const segmentsRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding-right: 8px;
  border-bottom: 1px solid var(--grn-color-shade3);
  gap: 8px;
  overflow: hidden;

  &[data-selected='true'] {
    background: var(--grn-color-background3);
  }
`;

const emailStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const nameStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  white-space: nowrap;
`;

const badgeBaseStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const activeBadgeStyles = css`
  ${badgeBaseStyles};
  background: rgba(121, 169, 148, 0.3);
  color: #466d5c;
`;

const neutralBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
`;

const fadedBadgeStyles = css`
  ${badgeBaseStyles};
  background: var(--grn-color-shade3);
  color: var(--grn-color-content2);
`;

const segmentBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  letter-spacing: -0.12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  color: var(--grn-color-content);
`;

const countBadgeStyles = css`
  ${segmentBadgeStyles};
  background: var(--grn-color-shade4);
  color: var(--grn-color-content);
  letter-spacing: -0.12px;
`;

const statusMenuWrapperStyles = css`
  position: relative;
`;

const statusMenuStyles = css`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 220px;
  padding: 8px 0;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow:
    0px 1px 1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0px 5.88px 14.05px rgba(0, 0, 0, 0.09);
`;

const statusMenuGroupStyles = css`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
`;

const statusMenuItemStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const statusMenuLabelStyles = css`
  color: var(--grn-color-content);
  flex-shrink: 0;
`;

const statusMenuCountStyles = css`
  flex: 1;
  color: var(--grn-color-content2);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const statusMenuCheckStyles = css`
  flex-shrink: 0;
  color: var(--grn-color-icon);
  display: flex;
  align-items: center;
  width: 18px;
  height: 18px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const segmentsGridStyles = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  width: 100%;
`;

const segmentCardStyles = css`
  display: flex;
  flex-direction: column;
  height: 208px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--grn-color-background);
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);
  cursor: pointer;
`;

const segmentCardCoverStyles = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
  text-align: center;
`;

const segmentCardFooterStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 18px;
  background: var(--grn-color-background);
`;

const segmentCountBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  background: var(--grn-color-shade3);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const pulseBorder = keyframes`
  0%, 100% { border-color: var(--grn-color-backgroundSuccess); }
  50% { border-color: transparent; }
`;

const liveBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  background: rgba(121, 169, 148, 0.3);
  border: 1px solid var(--grn-color-backgroundSuccess);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: #466d5c;
  white-space: nowrap;
  cursor: pointer;
  animation: ${pulseBorder} 3.36s ease-in-out infinite;
`;

const pulseFill = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const liveDotStyles = css`
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--grn-color-backgroundSuccessSubtle);
    animation: ${pulseFill} 3.36s ease-in-out infinite;
    animation-delay: var(--pulse-delay, 0s);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #79a994;
  }
`;

const clickableBadgeStyles = css`
  cursor: pointer;
`;

const pulseDotColor = keyframes`
  0%, 100% { background: var(--grn-color-backgroundSuccess); }
  50% { background: var(--grn-color-backgroundSuccessSubtle); }
`;

const livePillStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  background: var(--grn-color-background);
  border: 1px solid var(--grn-color-border);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const livePillDotStyles = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--grn-color-backgroundSuccess);
  animation: ${pulseDotColor} 3.36s ease-in-out infinite;
  flex-shrink: 0;
`;

const segmentsTableStyles = css`
  display: flex;
  width: 100%;
`;

const segmentNameColumnStyles = css`
  ${columnStyles};
  flex: 1;
`;

const segmentNumberColumnStyles = css`
  ${columnStyles};
  width: 140px;
`;

const segmentActionColumnStyles = css`
  ${columnStyles};
  width: 52px;
`;

const segmentTableRowStyles = css`
  display: flex;
  align-items: center;
  height: 64px;
  border-bottom: 1px solid var(--grn-color-border);
  width: 100%;
`;

const segmentTableTextStyles = css`
  padding-left: 8px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content);
`;

const segmentTagStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const segmentDetailTitleBlockStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const segmentDetailDescriptionStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  max-width: 360px;
`;

const titleUnderlineStyles = css`
  position: relative;
  border-bottom: 1px dashed var(--grn-color-content);
  cursor: help;
`;

const titleTooltipStyles = css`
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  z-index: 20;
  width: 256px;
  padding: 11px 16px;
  border-radius: var(--grn-radius-s);
  background: var(--grn-color-content);
  color: var(--grn-color-shade2);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  white-space: normal;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--grn-transition-fast),
    visibility var(--grn-transition-fast);
  pointer-events: none;
`;

const titleTooltipVisibleStyles = css`
  opacity: 1;
  visibility: visible;
`;

const segmentDetailTitleStyles = css`
  display: flex;
  align-items: center;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 36px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const loadingOverlayStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 0;
`;

const paginationWrapperStyles = css`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;

const linkStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content2);
  text-decoration: underline;
  cursor: pointer;
`;

type Subscriber = {
  email: string;
  name: string;
  status: string;
  statusType: 'active' | 'neutral' | 'faded';
  lastActivity: string;
  isResendLink?: boolean;
  totalSales: string;
  segments: { label: string; color: string }[];
  extraSegments?: number;
  dateArchived?: string;
  archiveReason?: string;
};

const PAGE_SIZE = 10;

type Segment = {
  name: string;
  color: string;
  subscribers: number;
  created: string;
  modified: string;
};

type UnengagedSubscriber = Subscriber & { daysSinceActivity: number };

const UNENGAGED_SUBSCRIBERS: UnengagedSubscriber[] = [
  {
    email: 'jane@austen.com',
    name: 'Jane Austen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Jan 10, 2026',
    daysSinceActivity: 100,
    totalSales: '$102.50',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 4,
  },
  {
    email: 'mark.twain@example.com',
    name: 'Mark Twain',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Dec 15, 2025',
    daysSinceActivity: 126,
    totalSales: '$76.30',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'charles.dickens@literature.net',
    name: 'Charles Dickens',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Nov 28, 2025',
    daysSinceActivity: 143,
    totalSales: '$89.90',
    segments: [{ label: 'May promotion', color: '#efc5c7' }],
  },
  {
    email: 'virginia.woolf@mail.com',
    name: 'Virginia Woolf',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Nov 10, 2025',
    daysSinceActivity: 161,
    totalSales: '$115.70',
    segments: [
      { label: 'Profile social form', color: '#fff98a' },
      { label: 'May promotion', color: '#cfddd7' },
    ],
  },
  {
    email: 'mark.twain@novelist.com',
    name: 'Mark Twain',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Oct 25, 2025',
    daysSinceActivity: 177,
    totalSales: '-',
    segments: [{ label: 'Profile social form', color: '#fff98a' }],
  },
  {
    email: 'virginia.woolf@modernist.com',
    name: 'Virginia Woolf',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Oct 5, 2025',
    daysSinceActivity: 197,
    totalSales: '-',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
  },
  {
    email: 'hemingway@writerhub.com',
    name: 'Ernest Hemingway',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Sep 20, 2025',
    daysSinceActivity: 212,
    totalSales: '-',
    segments: [
      { label: 'Profile soc...', color: '#f5f5e4' },
      { label: 'May prom...', color: '#9ebbed' },
    ],
    extraSegments: 4,
  },
  {
    email: 'leo.tolstoy@literature.org',
    name: 'Leo Tolstoy',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Aug 15, 2025',
    daysSinceActivity: 248,
    totalSales: '$63.40',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
  },
  {
    email: 'f.scott.fitzgerald@classicwriter.com',
    name: 'F. Scott Fitzgerald',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Jul 20, 2025',
    daysSinceActivity: 274,
    totalSales: '$120.00',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'charles.dickens@example.com',
    name: 'Charles Dickens',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Jun 30, 2025',
    daysSinceActivity: 294,
    totalSales: '$54.20',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'oscar.wilde@aesthetic.co',
    name: 'Oscar Wilde',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'May 18, 2025',
    daysSinceActivity: 337,
    totalSales: '$78.20',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'emily.bronte@moorlands.net',
    name: 'Emily Brontë',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Apr 25, 2025',
    daysSinceActivity: 360,
    totalSales: '$44.90',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
];

const SEGMENTS: Segment[] = [
  { name: 'All subscribers', color: '#f3f2f1', subscribers: 1034, created: '–', modified: '–' },
  {
    name: 'Subscribers unengaged for 90 or more days',
    color: '#f3f2f1',
    subscribers: 38,
    created: '–',
    modified: '–',
  },
  {
    name: 'Subscribers unengaged for 180 or more days',
    color: '#f3f2f1',
    subscribers: 38,
    created: '–',
    modified: '–',
  },
  {
    name: 'Instagram guide',
    color: '#e17569',
    subscribers: 56,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
  {
    name: 'IG Link-in-bio Form Opt-in',
    color: '#e8c7c8',
    subscribers: 77,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
  {
    name: 'QR Code subscribers',
    color: '#f9dedc',
    subscribers: 34,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
  { name: 'Referral', color: '#f8db58', subscribers: 27, created: 'Apr 1', modified: '1 hour ago' },
  {
    name: 'Manual upload',
    color: '#fef999',
    subscribers: 839,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
  { name: 'OPS', color: '#f5ede4', subscribers: 1034, created: 'Apr 1', modified: '1 hour ago' },
  {
    name: 'New clients - Zapier',
    color: '#e1d5c0',
    subscribers: 1034,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
  {
    name: 'Cancelled members',
    color: '#d2a751',
    subscribers: 1034,
    created: 'Apr 1',
    modified: '1 hour ago',
  },
];

const SUBSCRIBERS: Subscriber[] = [
  {
    email: 'jane@austen.com',
    name: 'Jane Austen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 12, 2026',
    totalSales: '$102.50',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 4,
  },
  {
    email: 'mark.twain@example.com',
    name: 'Mark Twain',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 10, 2026',
    totalSales: '$76.30',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'May promotion', color: '#def2ea' },
    ],
  },
  {
    email: 'sofia.ramirez@inbox.com',
    name: 'Sofia Ramirez',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 18, 2026',
    totalSales: '$214.00',
    segments: [{ label: 'VIP customers', color: '#c5e1f5' }],
    extraSegments: 2,
  },
  {
    email: 'liam.chen@webmail.co',
    name: 'Liam Chen',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 22, 2026',
    totalSales: '$48.90',
    segments: [
      { label: 'Profile social form', color: '#e8c0fd' },
      { label: 'Newsletter', color: '#cfddd7' },
    ],
  },
  {
    email: 'emma.johnson@mail.org',
    name: 'Emma Johnson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 15, 2026',
    totalSales: '$163.20',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'noah.williams@proton.me',
    name: 'Noah Williams',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 20, 2026',
    totalSales: '$91.00',
    segments: [
      { label: 'Newsletter', color: '#cfddd7' },
      { label: 'May promotion', color: '#def2ea' },
    ],
  },
  {
    email: 'olivia.martinez@gmail.com',
    name: 'Olivia Martinez',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 8, 2026',
    totalSales: '$330.75',
    segments: [{ label: 'VIP customers', color: '#c5e1f5' }],
    extraSegments: 3,
  },
  {
    email: 'ethan.kumar@outlook.com',
    name: 'Ethan Kumar',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 19, 2026',
    totalSales: '$55.00',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'ava.thompson@hey.com',
    name: 'Ava Thompson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 14, 2026',
    totalSales: '$127.40',
    segments: [
      { label: 'Newsletter', color: '#cfddd7' },
      { label: 'May promotion', color: '#def2ea' },
    ],
    extraSegments: 1,
  },
  {
    email: 'lucas.anderson@fastmail.com',
    name: 'Lucas Anderson',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 21, 2026',
    totalSales: '$82.10',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'mia.garcia@yahoo.com',
    name: 'Mia Garcia',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 11, 2026',
    totalSales: '$199.99',
    segments: [
      { label: 'VIP customers', color: '#c5e1f5' },
      { label: 'Profile social form', color: '#e8c0fd' },
    ],
  },
  {
    email: 'jackson.lee@icloud.com',
    name: 'Jackson Lee',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 17, 2026',
    totalSales: '$64.50',
    segments: [{ label: 'Newsletter', color: '#cfddd7' }],
  },
  {
    email: 'isabella.clark@mail.com',
    name: 'Isabella Clark',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 9, 2026',
    totalSales: '$145.80',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
    extraSegments: 2,
  },
  {
    email: 'aiden.wright@zoho.com',
    name: 'Aiden Wright',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 23, 2026',
    totalSales: '$38.25',
    segments: [{ label: 'Profile social form', color: '#e8c0fd' }],
  },
  {
    email: 'harper.king@proton.me',
    name: 'Harper King',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 13, 2026',
    totalSales: '$256.00',
    segments: [
      { label: 'VIP customers', color: '#c5e1f5' },
      { label: 'Newsletter', color: '#cfddd7' },
    ],
  },
  {
    email: 'benjamin.scott@gmail.com',
    name: 'Benjamin Scott',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Mar 16, 2026',
    totalSales: '$71.90',
    segments: [{ label: 'May promotion', color: '#def2ea' }],
  },
  {
    email: 'charles.dickens@literature.net',
    name: 'Charles Dickens',
    status: 'Active',
    statusType: 'active',
    lastActivity: 'Nov 28, 2025',
    totalSales: '$89.90',
    segments: [{ label: 'May promotion', color: '#efc5c7' }],
  },
  {
    email: 'virginia.woolf@mail.com',
    name: 'Virginia Woolf',
    status: 'Bounced',
    statusType: 'faded',
    lastActivity: 'Feb 28, 2026',
    totalSales: '$115.70',
    segments: [
      { label: 'Profile social form', color: '#fff98a' },
      { label: 'May promotion', color: '#cfddd7' },
    ],
  },
  {
    email: 'hemingway@writerhub.com',
    name: 'Ernest Hemingway',
    status: 'Archived',
    statusType: 'faded',
    lastActivity: 'Jan 15, 2026',
    totalSales: '-',
    segments: [
      { label: 'Profile social form', color: '#f5f5e4' },
      { label: 'May promotion', color: '#9ebbed' },
    ],
    extraSegments: 4,
    dateArchived: 'Feb 10, 2026',
    archiveReason: 'Archived via Welcome workflow',
  },
  {
    email: 'virginia.woolf@modernist.com',
    name: 'Virginia Woolf',
    status: 'Marked spam',
    statusType: 'faded',
    lastActivity: 'Mar 1, 2026',
    totalSales: '-',
    segments: [{ label: 'May promotion', color: '#ffdddb' }],
  },
];

function getStatusStyles(type: 'active' | 'neutral' | 'faded') {
  if (type === 'active') return activeBadgeStyles;
  if (type === 'neutral') return neutralBadgeStyles;
  return fadedBadgeStyles;
}

function StatusBadge({ status, type }: { status: string; type: 'active' | 'neutral' | 'faded' }) {
  return <span css={getStatusStyles(type)}>{status}</span>;
}

type SortColumn = 'subscriber' | 'status' | 'lastActivity' | 'totalSales' | 'segments';

function parseDate(dateStr: string): number {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function parseSales(salesStr: string): number {
  if (salesStr === '-') return 0;
  return parseFloat(salesStr.replace('$', '')) || 0;
}

function sortSubscribers(list: Subscriber[], column: SortColumn, ascending: boolean): Subscriber[] {
  const sorted = [...list];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'subscriber':
        cmp = a.email.localeCompare(b.email);
        break;
      case 'status':
        cmp = a.status.localeCompare(b.status);
        break;
      case 'lastActivity':
        cmp = parseDate(a.lastActivity) - parseDate(b.lastActivity);
        break;
      case 'totalSales':
        cmp = parseSales(a.totalSales) - parseSales(b.totalSales);
        break;
      default:
        return 0;
    }
    return ascending ? cmp : -cmp;
  });
  return sorted;
}

export default function Subscribers() {
  const location = useLocation<{ openSegmentDays?: number } | undefined>();
  const initialSegmentDays = location.state?.openSegmentDays ?? null;
  const [sortColumn, setSortColumn] = useState<SortColumn>('subscriber');
  const [ascending, setAscending] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [slowMode, setSlowMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'customers' | 'segments'>(
    initialSegmentDays !== null ? 'segments' : 'subscribers',
  );
  const [segmentsView, setSegmentsView] = useState<'thumbnail' | 'list'>('thumbnail');
  const [selectedSegmentDays, setSelectedSegmentDays] = useState<number | null>(initialSegmentDays);
  const [segmentFilterOpen, setSegmentFilterOpen] = useState(false);
  const [segmentsSearchMode, setSegmentsSearchMode] = useState(true);
  const [detailSearchActive, setDetailSearchActive] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [bulkSelected, setBulkSelected] = useState(false);
  const [unengagedBadgeVariant, setUnengagedBadgeVariant] = useState<'live' | 'dot' | 'pill'>(
    'dot',
  );
  const [sortLiveMode, setSortLiveMode] = useState(false);

  const ACTION_VERB_PAST: Record<string, string> = {
    Delete: 'deleted',
    Unsubscribe: 'unsubscribed',
    Archive: 'archived',
    Unarchive: 'unarchived',
  };

  const ACTION_VERB_GERUND: Record<string, string> = {
    Delete: 'Deleting',
    Unsubscribe: 'Unsubscribing',
    Archive: 'Archiving',
    Unarchive: 'Unarchiving',
  };

  const showActionToast = (action: string) => {
    const verb = ACTION_VERB_PAST[action];
    if (!verb) return;
    setToastMessage(`Subscribers are being ${verb}. We'll email you when it's done.`);
    setToastOpen(true);
  };

  const triggerBulkAction = (action: string) => {
    if (ACTION_VERB_PAST[action]) {
      setConfirmAction(action);
      return;
    }
    showActionToast(action);
  };

  const confirmBulkAction = () => {
    if (!confirmAction) return;
    showActionToast(confirmAction);
    setConfirmAction(null);
    setSelectedEmails([]);
    setBulkSelected(false);
  };
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!headerMenuOpen) return undefined;
    const handleClick = (e: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target as Node)) {
        setHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [headerMenuOpen]);

  const toggleEmailSelection = (email: string) => {
    setBulkSelected(false);
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email],
    );
  };

  const clearSelection = () => {
    setSelectedEmails([]);
    setBulkSelected(false);
  };

  useEffect(() => {
    setSelectedEmails([]);
  }, [activeTab, selectedSegmentDays]);
  const isCustomersTab = activeTab === 'customers';
  const isSegmentsTab = activeTab === 'segments';
  const isSegmentDetailView = selectedSegmentDays !== null;

  useEffect(() => {
    if (!isSegmentDetailView) return undefined;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [isSegmentDetailView, selectedSegmentDays]);
  const statusMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!statusMenuOpen) {
      return undefined;
    }
    const handleClick = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusMenuOpen]);

  const triggerLoading = useCallback(() => {
    if (!slowMode) {
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [slowMode]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SUBSCRIBERS.forEach(s => {
      counts[s.status] = (counts[s.status] || 0) + 1;
    });
    return counts;
  }, []);

  const handleStatusSelect = (status: string | null) => {
    setSelectedStatus(status);
    setActiveFilters([]);
    setCurrentPage(1);
    setStatusMenuOpen(false);
    triggerLoading();
  };

  const displayedSegments = segmentsSearchMode
    ? SEGMENTS.filter(s => !/180 or more days/.test(s.name)).map(s =>
        /90 or more days/.test(s.name) ? { ...s, name: 'Unengaged subscribers' } : s,
      )
    : SEGMENTS;

  const handleSegmentClick = (seg: Segment) => {
    if (segmentsSearchMode) {
      setSelectedSegmentDays(90);
      return;
    }
    const match = seg.name.match(/(\d+) or more days/);
    if (match) setSelectedSegmentDays(parseInt(match[1], 10));
  };

  const handleSort = (column: SortColumn) => {
    if (column === 'segments') return;
    if (sortColumn === column) {
      setAscending(!ascending);
    } else {
      setSortColumn(column);
      setAscending(true);
    }
  };

  const filtered = useMemo(() => {
    if (isSegmentDetailView && selectedSegmentDays !== null) {
      return UNENGAGED_SUBSCRIBERS.filter(s => s.daysSinceActivity >= selectedSegmentDays);
    }
    if (isCustomersTab) {
      return SUBSCRIBERS.filter(s => s.totalSales !== '-');
    }
    if (activeFilters.length > 0) {
      return SUBSCRIBERS.filter(s => activeFilters.includes(s.status));
    }
    if (selectedStatus) {
      return SUBSCRIBERS.filter(s => s.status === selectedStatus);
    }
    return SUBSCRIBERS;
  }, [activeFilters, selectedStatus, isCustomersTab, isSegmentDetailView, selectedSegmentDays]);

  const sorted = useMemo(
    () => sortSubscribers(filtered, sortColumn, ascending),
    [filtered, sortColumn, ascending],
  );

  const bulkButtons = useMemo(() => {
    const byEmail = new Map(sorted.map(s => [s.email, s]));
    const statuses = new Set<string>();
    selectedEmails.forEach(email => {
      const sub = byEmail.get(email);
      if (sub) statuses.add(sub.status);
    });
    const buttons = new Set<string>();
    statuses.forEach(status => {
      (STATUS_ACTIONS[status] || []).forEach(b => buttons.add(b));
    });
    return BUTTON_ORDER.filter(b => buttons.has(b));
  }, [selectedEmails, sorted]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = useMemo(
    () => sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sorted, currentPage],
  );

  const isArchivedView = selectedStatus === 'Archived';

  const arrowIcon = <IconArrowDown />;

  return (
    <div css={pageStyles} className="grn-context">
      <Header
        logoVariant={slowMode ? 'slowdesk' : 'flodesk'}
        onLogoClick={() => {
          setSlowMode(prev => !prev);
          if (slowMode) setLoading(false);
        }}
      />
      <div css={contentStyles}>
        <div css={contentInnerStyles}>
          {isSegmentDetailView && (
            <div>
              <TextButton
                icon={<IconArrowLeft />}
                onClick={() => {
                  setSelectedSegmentDays(null);
                  setDetailSearchActive(false);
                }}
              >
                Back
              </TextButton>
            </div>
          )}
          <div css={pageHeaderStyles}>
            {isSegmentDetailView ? (
              <>
                <div css={segmentDetailTitleBlockStyles}>
                  {(() => {
                    const titleText = segmentsSearchMode
                      ? 'Unengaged subscribers'
                      : `Subscribers unengaged for ${selectedSegmentDays} or more days`;
                    return (
                      <div css={segmentDetailTitleStyles}>
                        {detailSearchActive ? (
                          <span
                            css={titleUnderlineStyles}
                            onMouseEnter={() => setTitleHovered(true)}
                            onMouseLeave={() => setTitleHovered(false)}
                          >
                            {titleText}
                            <span
                              css={[titleTooltipStyles, titleHovered && titleTooltipVisibleStyles]}
                            >
                              Subscribers you&apos;ve sent at least 3 emails in the last{' '}
                              {selectedSegmentDays} days but haven&apos;t opened or clicked any
                            </span>
                          </span>
                        ) : (
                          titleText
                        )}
                      </div>
                    );
                  })()}
                  {!detailSearchActive && (
                    <div css={segmentDetailDescriptionStyles}>
                      Subscribers you&apos;ve sent at least 3 emails in the last{' '}
                      {selectedSegmentDays} days but haven&apos;t opened or clicked any
                    </div>
                  )}
                </div>
                <div css={filtersStyles}>
                  <TextButton
                    icon={<IconSearch />}
                    onClick={() => setDetailSearchActive(prev => !prev)}
                  >
                    Search
                  </TextButton>
                  <TextButton onClick={() => setSegmentFilterOpen(true)}>Filter</TextButton>
                  <TextButton icon={<IconDownload />}>Download CSV</TextButton>
                </div>
              </>
            ) : (
              <>
                <div css={tabsStyles}>
                  <span
                    css={activeTab === 'subscribers' ? tabActiveStyles : tabInactiveStyles}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setActiveTab('subscribers');
                      setCurrentPage(1);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setActiveTab('subscribers');
                        setCurrentPage(1);
                      }
                    }}
                  >
                    Subscribers
                  </span>
                  <span
                    css={activeTab === 'customers' ? tabActiveStyles : tabInactiveStyles}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setActiveTab('customers');
                      setCurrentPage(1);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setActiveTab('customers');
                        setCurrentPage(1);
                      }
                    }}
                  >
                    Customers
                  </span>
                  <span
                    css={activeTab === 'segments' ? tabActiveStyles : tabInactiveStyles}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setActiveTab('segments');
                      setCurrentPage(1);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setActiveTab('segments');
                        setCurrentPage(1);
                      }
                    }}
                  >
                    Segments
                  </span>
                </div>
                <div css={filtersStyles}>
                  <TextButton
                    icon={<IconSearch />}
                    onClick={() => {
                      if (isSegmentsTab) setSegmentsSearchMode(prev => !prev);
                    }}
                  >
                    Search
                  </TextButton>
                  <TextButton
                    icon={<IconChevronDown />}
                    iconPosition="right"
                    onClick={() => {
                      if (isSegmentsTab) setSortLiveMode(prev => !prev);
                    }}
                  >
                    Sort
                  </TextButton>
                  {!isCustomersTab && !isSegmentsTab && (
                    <div css={statusMenuWrapperStyles} ref={statusMenuRef}>
                      <TextButton
                        icon={<IconChevronDown />}
                        iconPosition="right"
                        onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                      >
                        Status
                      </TextButton>
                      {statusMenuOpen && (
                        <div css={statusMenuStyles}>
                          <div css={statusMenuGroupStyles}>
                            <div
                              css={statusMenuItemStyles}
                              role="button"
                              tabIndex={0}
                              onClick={() => handleStatusSelect(null)}
                              onKeyDown={e => e.key === 'Enter' && handleStatusSelect(null)}
                            >
                              <span css={statusMenuLabelStyles}>All</span>
                              <span css={statusMenuCountStyles}>{SUBSCRIBERS.length}</span>
                              {activeFilters.length === 0 && selectedStatus === null && (
                                <span css={statusMenuCheckStyles}>
                                  <IconCheck />
                                </span>
                              )}
                            </div>
                            {[
                              'Active',
                              'Unconfirmed',
                              'Archived',
                              'Bounced',
                              'Unsubscribed',
                              'Marked spam',
                              'Cleaned',
                            ].map(status => (
                              <div
                                key={status}
                                css={statusMenuItemStyles}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleStatusSelect(status)}
                                onKeyDown={e => e.key === 'Enter' && handleStatusSelect(status)}
                              >
                                <span css={statusMenuLabelStyles}>{status}</span>
                                <span css={statusMenuCountStyles}>{statusCounts[status] || 0}</span>
                                {activeFilters.length === 0 && selectedStatus === status && (
                                  <span css={statusMenuCheckStyles}>
                                    <IconCheck />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {!isCustomersTab && !isSegmentsTab && (
                    <>
                      {activeFilters.length > 0 ? (
                        <TextButton
                          icon={
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={e => {
                                e.stopPropagation();
                                setActiveFilters([]);
                                setCurrentPage(1);
                                triggerLoading();
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  setActiveFilters([]);
                                  setCurrentPage(1);
                                  triggerLoading();
                                }
                              }}
                              css={css`
                                display: flex;
                                align-items: center;
                                cursor: pointer;
                              `}
                            >
                              <IconCross />
                            </span>
                          }
                          iconPosition="right"
                          onClick={() => setFilterOpen(true)}
                        >
                          {sorted.length} results
                        </TextButton>
                      ) : (
                        <TextButton onClick={() => setFilterOpen(true)}>Filter</TextButton>
                      )}
                      <IconButton icon={<IconEllipsis />} />
                      <div css={buttonWrapperStyles}>
                        <Button variant="accent" icon={<IconPlus />}>
                          Add subscriber
                        </Button>
                      </div>
                    </>
                  )}
                  {isSegmentsTab && !isSegmentDetailView && (
                    <>
                      <IconToggleGroup>
                        <IconToggle
                          icon={<IconSquare />}
                          isActive={segmentsView === 'thumbnail'}
                          onClick={() => setSegmentsView('thumbnail')}
                        />
                        <IconToggle
                          icon={<IconListView />}
                          isActive={segmentsView === 'list'}
                          onClick={() => setSegmentsView('list')}
                        />
                      </IconToggleGroup>
                      <div css={buttonWrapperStyles}>
                        <Button variant="accent" icon={<IconPlus />}>
                          New segment
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div
            css={css`
              position: relative;
            `}
          >
            {!(isSegmentsTab && !isSegmentDetailView) && (
              <div
                css={[
                  bulkActionsBarStyles,
                  css`
                    position: absolute;
                    bottom: calc(100% + 8px);
                    left: 0;
                  `,
                ]}
                data-visible={selectedEmails.length > 0}
              >
                <span css={bulkActionsLabelStyles}>{selectedEmails.length} selected</span>
                {bulkButtons.map(btn => (
                  <Button
                    key={btn}
                    variant="neutral"
                    css={
                      btn === 'Archive' || btn === 'Unarchive' ? highlightButtonStyles : undefined
                    }
                    onClick={() => triggerBulkAction(btn)}
                  >
                    {btn}
                  </Button>
                ))}
              </div>
            )}

            {(() => {
              if (loading) {
                return (
                  <div css={loadingOverlayStyles}>
                    <Spinner
                      css={css`
                        width: 24px;
                        height: 24px;
                      `}
                      size={24}
                    />
                  </div>
                );
              }
              if (isSegmentsTab && !isSegmentDetailView) {
                return segmentsView === 'thumbnail' ? (
                  <div css={segmentsGridStyles}>
                    {displayedSegments.map(seg => (
                      <div
                        key={seg.name}
                        css={segmentCardStyles}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSegmentClick(seg)}
                        onKeyDown={e => e.key === 'Enter' && handleSegmentClick(seg)}
                      >
                        <div css={segmentCardCoverStyles} style={{ background: seg.color }}>
                          {seg.name}
                        </div>
                        <div css={segmentCardFooterStyles}>
                          {(() => {
                            const sortLiveSegments = [
                              'Instagram guide',
                              'Referral',
                              'OPS',
                              'QR Code subscribers',
                            ];
                            const isUnengaged = seg.name === 'Unengaged subscribers';
                            const isLiveSegment =
                              isUnengaged || (sortLiveMode && sortLiveSegments.includes(seg.name));
                            if (!isLiveSegment) {
                              return (
                                <span css={segmentCountBadgeStyles}>
                                  {seg.subscribers} subscribers
                                </span>
                              );
                            }
                            const pulseDelay = `-${performance.now() % 3360}ms`;
                            const cycleVariant = (e: React.SyntheticEvent) => {
                              e.stopPropagation();
                              setUnengagedBadgeVariant(prev => {
                                if (prev === 'live') return 'dot';
                                if (prev === 'dot') return 'pill';
                                return 'live';
                              });
                            };
                            const onBadgeClick = isUnengaged ? cycleVariant : undefined;
                            const badgeCss = isUnengaged
                              ? [segmentCountBadgeStyles, clickableBadgeStyles]
                              : segmentCountBadgeStyles;
                            if (unengagedBadgeVariant === 'dot') {
                              return (
                                <>
                                  <span
                                    css={badgeCss}
                                    role="button"
                                    tabIndex={isUnengaged ? 0 : -1}
                                    onClick={onBadgeClick}
                                    onKeyDown={e => {
                                      if (isUnengaged && e.key === 'Enter') cycleVariant(e);
                                    }}
                                  >
                                    {seg.subscribers} subscribers
                                  </span>
                                  <span
                                    css={liveDotStyles}
                                    style={{ '--pulse-delay': pulseDelay } as React.CSSProperties}
                                  />
                                </>
                              );
                            }
                            if (unengagedBadgeVariant === 'pill') {
                              return (
                                <>
                                  <span
                                    css={badgeCss}
                                    role="button"
                                    tabIndex={isUnengaged ? 0 : -1}
                                    onClick={onBadgeClick}
                                    onKeyDown={e => {
                                      if (isUnengaged && e.key === 'Enter') cycleVariant(e);
                                    }}
                                  >
                                    {seg.subscribers} subscribers
                                  </span>
                                  <span css={livePillStyles}>
                                    <span
                                      css={livePillDotStyles}
                                      style={{ animationDelay: pulseDelay }}
                                    />
                                    Live
                                  </span>
                                </>
                              );
                            }
                            return (
                              <span
                                css={liveBadgeStyles}
                                role="button"
                                tabIndex={isUnengaged ? 0 : -1}
                                onClick={onBadgeClick}
                                onKeyDown={e => {
                                  if (isUnengaged && e.key === 'Enter') cycleVariant(e);
                                }}
                                style={{ animationDelay: pulseDelay }}
                              >
                                {seg.subscribers} subscribers
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div css={segmentsTableStyles}>
                    <div css={segmentNameColumnStyles}>
                      <TextButton trimSide="left">Segments</TextButton>
                      <div css={rowsContainerStyles}>
                        {displayedSegments.map(seg => (
                          <div
                            key={seg.name}
                            css={segmentTableRowStyles}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSegmentClick(seg)}
                            onKeyDown={e => e.key === 'Enter' && handleSegmentClick(seg)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span css={segmentTagStyles} style={{ background: seg.color }}>
                              {seg.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={segmentNumberColumnStyles}>
                      <TextButton trimSide="left">Subscribers</TextButton>
                      <div css={rowsContainerStyles}>
                        {displayedSegments.map(seg => (
                          <div key={seg.name} css={segmentTableRowStyles}>
                            <span css={segmentTableTextStyles}>{seg.subscribers}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={segmentNumberColumnStyles}>
                      <TextButton trimSide="left">Created</TextButton>
                      <div css={rowsContainerStyles}>
                        {displayedSegments.map(seg => (
                          <div key={seg.name} css={segmentTableRowStyles}>
                            <span css={segmentTableTextStyles}>{seg.created}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={segmentNumberColumnStyles}>
                      <TextButton trimSide="left">Modified</TextButton>
                      <div css={rowsContainerStyles}>
                        {displayedSegments.map(seg => (
                          <div key={seg.name} css={segmentTableRowStyles}>
                            <span css={segmentTableTextStyles}>{seg.modified}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div css={segmentActionColumnStyles}>
                      <div
                        css={css`
                          height: 36px;
                        `}
                      />
                      <div css={rowsContainerStyles}>
                        {displayedSegments.map(seg => (
                          <div key={seg.name} css={segmentTableRowStyles}>
                            <IconButton icon={<IconEllipsis />} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <>
                  <div css={tableStyles}>
                    <div css={subscriberColumnStyles}>
                      <div css={headerCheckboxRowStyles} ref={headerMenuRef}>
                        <span
                          className="row-checkbox"
                          css={rowCheckboxStyles}
                          data-checked={selectedEmails.length > 0}
                        >
                          <Checkbox.Box
                            checked={bulkSelected && selectedEmails.length > 0}
                            isIndeterminate={!bulkSelected && selectedEmails.length > 0}
                            onChange={() => setHeaderMenuOpen(prev => !prev)}
                          />
                        </span>
                        <TextButton
                          icon={sortColumn === 'subscriber' ? arrowIcon : undefined}
                          iconPosition="right"
                          trimSide="left"
                          onClick={() => handleSort('subscriber')}
                        >
                          Subscriber
                        </TextButton>
                        {headerMenuOpen && (
                          <div css={headerCheckboxMenuStyles}>
                            <div
                              css={headerCheckboxMenuItemStyles}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setSelectedEmails(paged.map(s => s.email));
                                setBulkSelected(true);
                                setHeaderMenuOpen(false);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  setSelectedEmails(paged.map(s => s.email));
                                  setBulkSelected(true);
                                  setHeaderMenuOpen(false);
                                }
                              }}
                            >
                              Select {paged.length} on this page
                            </div>
                            <div
                              css={headerCheckboxMenuItemStyles}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setSelectedEmails(sorted.map(s => s.email));
                                setBulkSelected(true);
                                setHeaderMenuOpen(false);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  setSelectedEmails(sorted.map(s => s.email));
                                  setBulkSelected(true);
                                  setHeaderMenuOpen(false);
                                }
                              }}
                            >
                              Select all {sorted.length}
                            </div>
                            {selectedEmails.length > 0 && (
                              <div
                                css={headerCheckboxMenuItemStyles}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  clearSelection();
                                  setHeaderMenuOpen(false);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    clearSelection();
                                    setHeaderMenuOpen(false);
                                  }
                                }}
                              >
                                Deselect all
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => {
                          const isChecked = selectedEmails.includes(s.email);
                          return (
                            <div key={s.email} css={rowStyles} data-selected={isChecked}>
                              <span
                                className="row-checkbox"
                                css={rowCheckboxStyles}
                                data-checked={isChecked}
                              >
                                <Checkbox.Box
                                  checked={isChecked}
                                  onChange={() => toggleEmailSelection(s.email)}
                                />
                              </span>
                              <span css={emailStyles}>{s.email}</span>
                              <span css={nameStyles}>{s.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div css={statusColumnStyles}>
                      <TextButton
                        icon={sortColumn === 'status' ? arrowIcon : undefined}
                        iconPosition="right"
                        trimSide="left"
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TextButton>
                      <div css={rowsContainerStyles}>
                        {paged.map(s => (
                          <div
                            key={s.email}
                            css={statusRowStyles}
                            data-selected={selectedEmails.includes(s.email)}
                          >
                            <StatusBadge status={s.status} type={s.statusType} />
                          </div>
                        ))}
                      </div>
                    </div>

                    {isArchivedView ? (
                      <>
                        <div css={activityColumnStyles}>
                          <TextButton trimSide="left">Date archived</TextButton>
                          <div css={rowsContainerStyles}>
                            {paged.map(s => (
                              <div
                                key={s.email}
                                css={activityRowStyles}
                                data-selected={selectedEmails.includes(s.email)}
                              >
                                <span>{s.dateArchived || '-'}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div css={reasonColumnStyles}>
                          <TextButton trimSide="left">Archive reason</TextButton>
                          <div css={rowsContainerStyles}>
                            {paged.map(s => (
                              <div
                                key={s.email}
                                css={reasonRowStyles}
                                data-selected={selectedEmails.includes(s.email)}
                              >
                                <span>{s.archiveReason || '-'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div css={activityColumnStyles}>
                          <TextButton
                            icon={sortColumn === 'lastActivity' ? arrowIcon : undefined}
                            iconPosition="right"
                            trimSide="left"
                            onClick={() => handleSort('lastActivity')}
                          >
                            Last activity
                          </TextButton>
                          <div css={rowsContainerStyles}>
                            {paged.map(s => (
                              <div
                                key={s.email}
                                css={activityRowStyles}
                                data-selected={selectedEmails.includes(s.email)}
                              >
                                {s.isResendLink ? (
                                  <span css={linkStyles}>Resend opt-in</span>
                                ) : (
                                  <span>{s.lastActivity}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div css={salesColumnStyles}>
                          <TextButton
                            icon={sortColumn === 'totalSales' ? arrowIcon : undefined}
                            iconPosition="right"
                            trimSide="left"
                            onClick={() => handleSort('totalSales')}
                          >
                            Total sales
                          </TextButton>
                          <div css={rowsContainerStyles}>
                            {paged.map(s => (
                              <div
                                key={s.email}
                                css={salesRowStyles}
                                data-selected={selectedEmails.includes(s.email)}
                              >
                                <span>{s.totalSales}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div css={segmentsColumnStyles}>
                          <TextButton trimSide="left">Segments</TextButton>
                          <div css={rowsContainerStyles}>
                            {paged.map(s => (
                              <div
                                key={s.email}
                                css={segmentsRowStyles}
                                data-selected={selectedEmails.includes(s.email)}
                              >
                                {s.segments.map(seg => (
                                  <span
                                    key={seg.label}
                                    css={segmentBadgeStyles}
                                    style={{ background: seg.color }}
                                  >
                                    {seg.label}
                                  </span>
                                ))}
                                {s.extraSegments && (
                                  <span css={countBadgeStyles}>+ {s.extraSegments}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div css={paginationWrapperStyles}>
                      <Pagination
                        amount={totalPages}
                        selected={currentPage}
                        onChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>
      <Toast isOpen={toastOpen} onDismiss={() => setToastOpen(false)} dismissTimeout={4000}>
        {toastMessage}
      </Toast>
      <Modal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        hasCloseButton
        title={confirmAction ? `Confirm ${confirmAction.toLowerCase()}` : undefined}
        description={
          confirmAction
            ? `${ACTION_VERB_GERUND[confirmAction]} ${selectedEmails.length} subscribers. This process might take a moment. We'll email you when it's done.`
            : undefined
        }
        cardMaxWidth="440px"
        className="grn-context"
      >
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            padding-top: 24px;
          `}
        >
          <Button variant="neutral" onClick={() => setConfirmAction(null)}>
            Cancel
          </Button>
          <Button variant="accent" onClick={confirmBulkAction}>
            {confirmAction}
          </Button>
        </div>
      </Modal>
      {segmentFilterOpen && selectedSegmentDays !== null && (
        <SegmentFilterModal
          days={selectedSegmentDays}
          onClose={() => setSegmentFilterOpen(false)}
        />
      )}
      {filterOpen && (
        <FilterModal
          initialStatuses={selectedStatus ? [selectedStatus] : activeFilters}
          onClose={() => setFilterOpen(false)}
          onApply={statuses => {
            setActiveFilters(statuses);
            setSelectedStatus(null);
            setCurrentPage(1);
            setFilterOpen(false);
            triggerLoading();
          }}
        />
      )}
    </div>
  );
}
